import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { API } from "../../../helpers/API/amplifyApi";
import dayjsTZ from "../../../utils/dayjs";
import { getCompanyTimezone } from "../../../utils/timezone/teamTimezone";
import { pickerToDayKey } from "../../../utils/timezone/dayBounds";
import { ensureDeferredTables } from "../../../helpers/API/ensureDeferredTables";
import {
  buildAnalyticsDashboard,
  isActiveEmployee,
} from "../utils/analyticsAggregates";
import { periodGrain, resolvePeriodRange } from "../utils/periodRange";

const toLocalDayString = (value) =>
  pickerToDayKey(value, getCompanyTimezone()) ||
  dayjsTZ(value).format("YYYY-MM-DD");

async function fetchAnalyticsClockings(timeInterval) {
  const filterClockingObj = {
    limiter: dayjsTZ(toLocalDayString(timeInterval[0]))
      .startOf("day")
      .valueOf(),
    endLimiter: dayjsTZ(toLocalDayString(timeInterval[1]))
      .add(1, "day")
      .endOf("day")
      .valueOf(),
  };
  return API.get("clocking", "/clocking", {
    queryStringParameters: { ...filterClockingObj },
  });
}

/**
 * Loads period clockings + builds chart aggregates.
 * @param {object} filters - year, quarter, month, department, team, employeeId, employeeScope
 */
export default function useAnalyticsData(filters) {
  const dispatch = useDispatch();
  const { employees } = useSelector((state) => state.employeesList);
  const { employeeRequests } = useSelector((state) => state.employeeRequests);
  const shifts = useSelector((state) => state.shiftsList?.shifts) || [];
  const [clockings, setClockings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const range = useMemo(
    () =>
      resolvePeriodRange({
        year: filters.year,
        quarter: filters.quarter,
        month: filters.month,
      }),
    [filters.year, filters.quarter, filters.month],
  );

  const grain = useMemo(
    () => periodGrain({ month: filters.month, quarter: filters.quarter }),
    [filters.month, filters.quarter],
  );

  useEffect(() => {
    ensureDeferredTables(dispatch, { employeeRequests }, [
      "employeeRequests",
    ]);
  }, [dispatch, employeeRequests]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await fetchAnalyticsClockings(range);
      const empById = new Map(
        (employees || []).map((e) => [e.employeeId, e]),
      );
      const enriched = (Array.isArray(rows) ? rows : []).map((c) => ({
        ...c,
        employeeDetails: empById.get(c.employeeId) || c.employeeDetails || {},
      }));
      setClockings(enriched);
    } catch (err) {
      console.error(err);
      setError(err);
      setClockings([]);
    } finally {
      setLoading(false);
    }
  }, [range, employees]);

  useEffect(() => {
    load();
  }, [load]);

  const dashboard = useMemo(() => {
    const startTs = range[0].startOf("day").valueOf();
    const endTs = range[1].endOf("day").valueOf();
    return buildAnalyticsDashboard({
      clockings,
      requests: employeeRequests || [],
      shifts: shifts || [],
      employees: employees || [],
      filters: {
        department: filters.department || null,
        team: filters.team || null,
        employeeId: filters.employeeId || null,
        employeeScope: filters.employeeScope || "active",
      },
      startTs,
      endTs,
      grain,
    });
  }, [
    clockings,
    employeeRequests,
    shifts,
    employees,
    filters.department,
    filters.team,
    filters.employeeId,
    filters.employeeScope,
    range,
    grain,
  ]);

  const filterOptions = useMemo(() => {
    const allEmps = (employees || []).map((e) => ({
      value: e.employeeId,
      label: `${e.employeeFirstName || ""} ${e.employeeLastName || ""}`.trim(),
      department: e.employeeDepartmentName,
      team: e.employeeTeam,
      active: isActiveEmployee(e),
    }));
    const scoped = allEmps.filter((e) => e.label);
    const depts = new Set();
    const teams = new Set();
    for (const e of scoped) {
      if (e.department) depts.add(e.department);
      if (e.team) teams.add(e.team);
    }
    return {
      departments: [...depts].sort(),
      teams: [...teams].sort(),
      employees: scoped.sort((a, b) => a.label.localeCompare(b.label, "sq")),
    };
  }, [employees]);

  return {
    loading,
    error,
    dashboard,
    filterOptions,
    range,
    reload: load,
  };
}
